import { deleteField, doc as docRef, setDoc } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { applyEdit, deepEqual, emptySections, setConfirmed, undoEdit, type Actor, type Field, type SectionId } from '@sonder/shared'
import { useAuth } from '@/auth/AuthProvider'
import { useMode } from '@/app/mode'
import { db } from '@/lib/firebase'
import { useDoc, useDocWriter } from './hooks'
import { applyOverlay, usePractice } from './practice'

export function getPath(obj: unknown, dotted: string): unknown {
  let cur: unknown = obj
  for (const k of dotted.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[k]
  }
  return cur
}

/**
 * Field-level editor for one section document. Each change becomes one dotted-path write,
 * so teammates editing different fields never overwrite each other. A local overlay keeps
 * typing responsive until the snapshot catches up.
 *
 * In practice mode (staff previewing as the client) every change acts as the client's and stays in the
 * practice store instead: nothing is written.
 */
export function useSectionEditor<T>(clientId: string, sectionId: SectionId) {
  const path = `clients/${clientId}/sections/${sectionId}`
  const { data, loading, error } = useDoc<T>(path)
  const writer = useDocWriter(path)
  const practice = usePractice()
  const { user } = useAuth()
  const mode = useMode()
  const actor = useMemo<Actor>(() => ({ kind: mode === 'client' || practice ? 'client' : 'staff', id: user?.uid ?? 'anon' }), [mode, practice, user?.uid])
  const [localOverlay, setLocalOverlay] = useState<Record<string, unknown>>({})
  const overlay = practice ? practice.overlay(path) : localOverlay
  const update = practice?.update
  const setOverlay = useCallback(
    (fn: (o: Record<string, unknown>) => Record<string, unknown>) => (update ? update(path, fn) : setLocalOverlay(fn)),
    [update, path],
  )
  const write = useCallback((updates: Record<string, unknown>) => (practice ? undefined : writer.write(updates)), [practice, writer])

  // A step added after this client was set up has no document yet, and edits are in-place updates.
  // Create the empty one so the step renders and its first edit lands.
  useEffect(() => {
    if (loading || data || error || practice) return
    setDoc(docRef(db, path), emptySections()[sectionId], { merge: true }).catch(() => undefined)
  }, [loading, data, error, path, sectionId, practice])

  // Drop overlay entries once the server value matches (or the doc changed underneath us).
  useEffect(() => {
    if (!data) return
    setOverlay((o) => {
      const next: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(o)) if (!deepEqual(getPath(data, k), v)) next[k] = v
      return Object.keys(next).length === Object.keys(o).length ? o : next
    })
  }, [data, setOverlay])

  /** Merged view of the doc with the overlay applied, for rendering lists. */
  const view = useMemo(() => (data ? applyOverlay(data, overlay) : null), [data, overlay])

  // Read through the merged view, so a field inside something just added (a new contact, say) reads back.
  const getRaw = useCallback((p: string): unknown => getPath(view, p), [view])
  const getField = useCallback(<V = string,>(p: string): Field<V> | undefined => getRaw(p) as Field<V> | undefined, [getRaw])

  const setValue = useCallback(
    <V,>(p: string, value: V) => {
      const next = applyEdit(getField<V>(p), value, actor)
      if (!next) return
      setOverlay((o) => ({ ...o, [p]: next }))
      write({ [p]: next })
    },
    [getField, actor, setOverlay, write],
  )

  const confirm = useCallback(
    (p: string, confirmed: boolean) => {
      const cur = getField(p)
      if (!cur) return
      const next = setConfirmed(cur, confirmed, actor)
      if (!next) return
      setOverlay((o) => ({ ...o, [p]: next }))
      write({ [p]: next })
    },
    [getField, actor, setOverlay, write],
  )

  /** "Undo changes": back to Sonder's version of a value the client edited. */
  const undo = useCallback(
    (p: string) => {
      const next = undoEdit(getField(p))
      if (!next) return
      setOverlay((o) => ({ ...o, [p]: next }))
      write({ [p]: next })
    },
    [getField, setOverlay, write],
  )

  /** Structural writes (order arrays, ids, timestamps) bypass the envelope. */
  const setRaw = useCallback(
    (p: string, value: unknown) => {
      setOverlay((o) => ({ ...o, [p]: value }))
      write({ [p]: value })
    },
    [setOverlay, write],
  )
  const remove = useCallback(
    (p: string) => {
      setOverlay((o) => ({ ...o, [p]: undefined }))
      write({ [p]: deleteField() })
    },
    [setOverlay, write],
  )

  return { doc: view, loading, error, getField, setValue, confirm, undo, setRaw, remove, status: practice ? ('idle' as const) : writer.status, flush: writer.flush, mode, actor }
}

export type SectionEditor<T> = ReturnType<typeof useSectionEditor<T>>
